// frontend/src/services/api.ts
// Allow reading env in TS without Node types
declare const process: any;
/**
 * API Service for Grippy AI Frontend
 * 
 * Handles auth + chat + history endpoints.
 */

let __grippy_isRedirectingToLogin = false;

export interface QueryRequest {
  query: string;
  session_id?: string;
}

export interface QueryResponse {
  success: boolean;
  message_id?: string;
  query?: string;
  response?: string;
  timestamp: string;
  session_id?: string;
  interaction_id?: string;
  data?: {
    tables: Array<{
      title: string;
      columns: string[];
      data: any[];
      row_count: number;
    }>;
    charts: Array<{
      title: string;
      type: 'plotly' | 'matplotlib';
      data: string;
      chart_id: string;
    }>;
    chat_responses: Array<{
      title: string;
      content: string;
    }>;
  };
  error?: string;
}

export interface ConversationMessage {
  type: 'user' | 'assistant';
  content: string;
  id: string;
  timestamp?: string;
}

export interface ConversationHistoryResponse {
  success: boolean;
  history: ConversationMessage[];
  session_id?: string;
}

export interface AuthUrlResponse {
  auth_url: string;
  state: string;
}

export interface TokenRequest {
  code: string;
  state?: string;
}

export interface TokenResponse {
  access_token: string;
  user_info: {
    id?: string;
    name?: string;
    email?: string;
    userPrincipalName?: string;
  };
}

export interface UserInfoResponse {
  success: boolean;
  user_info?: {
    id?: string;
    name?: string;
    email?: string;
    userPrincipalName?: string;
  };
  error?: string;
}

/** ---- History DTOs (match backend responses) ---- */
export interface HistorySession {
  session_id: string;          // e.g. "session_1759935436288_b24n11z"
  title: string;
  is_favorite?: boolean;
  created_at?: string;
  modified_at?: string;
  message_count?: number;
  last_interaction_at?: string;
}

export interface HistoryInteraction {
  interaction_id: string;      // GUID
  request_id?: string;
  user_question: string;
  grippy_answer?: string | null;
  dataframe_json?: string | null;
  figure_json?: string | null;
  image_url?: string | null;
  is_liked?: boolean | null;
  is_disliked?: boolean | null;
  is_flagged?: boolean | null;
  flagged_reason?: string | null;
  created_at?: string;
}

export interface ListSessionsResponse {
  success: boolean;
  sessions: HistorySession[];
}

export interface GetSessionResponse {
  success: boolean;
  session: HistorySession;
  interactions: HistoryInteraction[];
}

class ApiService {
  private baseUrl: string;
  private sessionId: string;
  private accessToken: string | null = null;
  // Emits window event when auth becomes invalid
  private handleUnauthorized() {
    // Auto-redirect to Microsoft login when backend says: 401 login_required
    // 1) prevent redirect loops
    if (__grippy_isRedirectingToLogin) return;
      __grippy_isRedirectingToLogin = true;
      // 2) clear local token
      this.logout();
      // 3) remember where to come back after login
      try {
        const back = window.location.pathname + window.location.search + window.location.hash;
        sessionStorage.setItem('postLoginRedirect', back);
      } catch {}
      // 4) ask backend for the MS login URL and navigate
      (async () => {
        try {
          const r = await fetch(`${this.baseUrl}/auth/login`, { credentials: 'include' });
          if (r.ok) {
            const data: { auth_url?: string } = await r.json();
            if (data?.auth_url) {
              window.location.href = data.auth_url;
              return;
            }
          }
        } catch { /* ignore and fall back */ }
        // Fallback: route-level login page (optional)
        window.location.href = '/login';
    })();
  }

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL ?? '';
    this.sessionId = this.generateSessionId();
    this.loadTokenFromStorage();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private loadTokenFromStorage(): void {
    try {
      const storedAuth = localStorage.getItem('grippy_auth');
      if (storedAuth) {
        const authData = JSON.parse(storedAuth);
        this.accessToken = authData.access_token;
      }
    } catch (error) {
      console.error('Error loading token from storage:', error);
    }
  }

  private saveTokenToStorage(accessToken: string, userInfo: any): void {
    try {
      const authData = {
        access_token: accessToken,
        user_info: userInfo,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('grippy_auth', JSON.stringify(authData));
      this.accessToken = accessToken;
    } catch (error) {
      console.error('Error saving token to storage:', error);
    }
  }

  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
    return headers;
  }

  /** -------------------- Auth -------------------- */
  async getAuthUrl(): Promise<AuthUrlResponse> {
    const res = await fetch(`${this.baseUrl}/auth/login`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async exchangeCodeForToken(code: string, state?: string): Promise<TokenResponse> {
    const res = await fetch(`${this.baseUrl}/auth/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, state } as TokenRequest),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data: TokenResponse = await res.json();
    this.saveTokenToStorage(data.access_token, data.user_info);
    return data;
  }

  async getCurrentUser(): Promise<UserInfoResponse> {
    try {
      const res = await fetch(`${this.baseUrl}/auth/user`, {
        headers: this.getAuthHeaders(),
        credentials: 'include',
      });
      if (res.status === 401) { this.handleUnauthorized(); return { success: false, error: 'login_required' }; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
    }
  }

  isAuthenticated(): boolean { return this.accessToken !== null; }
  logout(): void { this.accessToken = null; localStorage.removeItem('grippy_auth'); }

  /** -------------------- Chat -------------------- */
  async sendQuery(query: string, signal?: AbortSignal): Promise<QueryResponse> {
    try {
      const res = await fetch(`${this.baseUrl}/api/query`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ query, session_id: this.sessionId } as QueryRequest),
        signal,
        credentials: 'include',
      });
      if (res.status === 401) { this.handleUnauthorized(); return {
        success: false,
        error: 'login_required',
        timestamp: new Date().toISOString(),
        session_id: this.sessionId,
      }; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    } catch (error) {
      return {
        success: false,
        error: `Failed to send query: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date().toISOString(),
        session_id: this.sessionId,
      };
    }
  }

  /** -------------------- Health -------------------- */
  async healthCheck(): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/health`);
      return res.ok;
    } catch {
      return false;
    }
  }

  /** -------------------- Session helpers -------------------- */
  getSessionId(): string { return this.sessionId; }
  resetSession(): void { this.sessionId = this.generateSessionId(); }
  setSessionId(id: string): void { this.sessionId = id; }

  /** -------------------- History endpoints -------------------- */
  async listSessions(): Promise<ListSessionsResponse> {
    const resp = await fetch(`${this.baseUrl}/api/history/sessions`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    if (resp.status === 401) { this.handleUnauthorized(); return { success: false, sessions: [] }; }
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const json = await resp.json();
    // Backend returns an array -> normalize
    if (Array.isArray(json)) {
      return { success: true, sessions: json as HistorySession[] };
    }
    // If you later change backend to wrap the payload, handle both shapes:
    if (json && Array.isArray(json.sessions)) {
      return { success: !!json.success, sessions: json.sessions };
    }
    return { success: false, sessions: [] };
  }

  async getSession(sessionId: string): Promise<GetSessionResponse> {
    const res = await fetch(`${this.baseUrl}/api/history/sessions/${encodeURIComponent(sessionId)}/interactions`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    if (res.status === 401) { this.handleUnauthorized(); return {
      success: false,
      session: { session_id: sessionId, title: '' },
      interactions: [],
    }; }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const interactions = await res.json();
    // Backend returns array directly, wrap it in expected format
    return {
      success: true,
      session: { session_id: sessionId, title: '' }, // Minimal session info
      interactions: interactions
    };
  }

  // Optional convenience methods (wire up later if you like)
  async renameSession(sessionId: string, title: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/history/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ title }),
    });
  }

  async favoriteSession(sessionId: string, is_favorite: boolean): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/history/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ is_favorite }),
    });
  }

  async deleteSession(sessionId: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/history/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
  }

  async likeInteraction(sessionId: string, interactionId: string, is_liked: boolean): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/history/interactions/${encodeURIComponent(interactionId)}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ is_liked }),
      credentials: 'include',
    });
    if (res.status === 401) { this.handleUnauthorized(); return; }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  }


  async reportInteraction(sessionId: string, interactionId: string, reason: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/history/interactions/${encodeURIComponent(interactionId)}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ is_flagged: true, flag_reason: reason }),
      credentials: 'include',
    });
    if (res.status === 401) { this.handleUnauthorized(); return; }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  }


  async deleteInteraction(sessionId: string, interactionId: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/history/sessions/${encodeURIComponent(sessionId)}/interactions/${encodeURIComponent(interactionId)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
  }

  // mock function to generate chart button in feedback component
  async generateChart(sessionId: string, interactionId: string): Promise<any> {
    const res = await fetch(`${this.baseUrl}/api/history/interactions/${encodeURIComponent(interactionId)}/generate-chart`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ session_id: sessionId }),
      credentials: 'include',
    });
    if (res.status === 401) { this.handleUnauthorized(); throw new Error('Unauthorized'); }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }
}

// Export a singleton instance
export const apiService = new ApiService();
export default apiService;
